import { ConfirmationDialog, IconAnchorButton, IconButton, Id, Trigger, useFormatDateTime } from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import styled from "styled-components";
import { DataFieldIndexResource } from "../../../../shared/types/dataField";
import { birthdayWidgetService } from "../../dashboard/widgets/birthdays/service";
import { InvitationListAttachSingleContactDrawer } from "../../events/eventDetails/invitationList/InvitationListAttachSingleContactDrawer";
import { contactService } from "../../shared/api/contactService";
import { TenantArchiveDrawer } from "../../shared/tenantArchive/TenantArchiveDrawer";
import { useArchiveByTenantHandler } from "../../shared/tenantArchive/useRestoreByTenantHandler";
import { IContactEmailUpdateResource, IContactPhoneUpdateResource, IContactUpdateResource } from "../../shared/types";
import {
	ViewPageSection,
	ViewPageSectionBody,
	ViewPageSectionHeader,
	ViewPageSectionListItem,
} from "../../shared/view";
import { useContactListUtils } from "../useContactListUtils";
import { AffiliationCountCard } from "./AffilationCountCard";
import { ContactTestDrivesLinks } from "./ContactTestDrivesLinks";
import { ContactUpdateBaseInfoDrawer } from "./ContactUpdateBaseInfoDrawer";
import { ContactUpdateDrawer } from "./ContactUpdateDrawer";
import { ContactViewModuleSection } from "./ContactViewModuleOveriew";
import { NewsletterListCard } from "./NewsletterListCard";

interface Props {
	contact: IContactUpdateResource;
	dataFields: DataFieldIndexResource[];
	contactViewId: Id;
}

const GeneralInfoContent = styled.div`
	display: grid;
	grid-template-columns: max-content 1fr;
	column-gap: ${({ theme }) => theme.spacing(5)};
	flex: 1;
	justify-items: start;
`;

const HeaderContent = styled.div`
	display: flex;
	column-gap: ${({ theme }) => theme.spacing(3)};
	min-width: 400px;
	align-items: center;
	justify-content: end;
`;

const Label = styled.span`
	font-weight: ${({ theme }) => theme.fonts.weights.bold};
`;

const Row: FC<{ label: string; value: string }> = ({ label, value }) => {
	return (
		<>
			<Label>{label}:</Label>
			<span>{value}</span>
		</>
	);
};

const getPrimary = (attribute: { isPrimary: boolean }[]) => {
	return attribute.find((x) => x.isPrimary) ?? null;
};

export const ContactViewGeneralSection: FC<Props> = ({ contact, dataFields, contactViewId }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { resolveAttributeValue } = useContactListUtils(null);
	const formatDateTime = useFormatDateTime();
	const queryClient = useQueryClient();
	const archiveByTenantHandler = useArchiveByTenantHandler(contactService, () => {
		void queryClient.invalidateQueries({ queryKey: birthdayWidgetService.key });
		navigate(-1);
	});

	const attributes = useMemo(
		() =>
			contact.attributes.map((attribute) => ({
				...attribute,
				label:
					attribute.type === "column" || attribute.type === "relation"
						? t(attribute.entityType as any)
						: (dataFields.find((x) => x.id === attribute.entityType)?.label ?? ""),
			})),
		[contact.attributes, dataFields, t],
	);

	const primaryEmail = useMemo(
		() => getPrimary(contact.contactEmails) as IContactEmailUpdateResource,
		[contact.contactEmails],
	);
	const primaryPhone = useMemo(
		() => getPrimary(contact.contactPhones) as IContactPhoneUpdateResource,
		[contact.contactPhones],
	);

	return (
		<ViewPageSection style={{ gridArea: "general" }}>
			<ViewPageSectionHeader>
				<HeaderContent>
					<Label style={{ marginRight: "auto" }}>{`${contact.firstName} ${contact.lastName}`}</Label>
					{primaryEmail && (
						<IconAnchorButton
							title={t("Send email to contact")}
							href={`mailto:${primaryEmail.email}`}
							iconSrc="/mail.svg"
							size="small"
						/>
					)}
					{primaryPhone && (
						<IconAnchorButton
							title={t("Call contact")}
							href={`tel:${primaryPhone.phoneNumber}`}
							iconSrc="/tel.svg"
							size="small"
						/>
					)}
					{contact.affiliatedCarFleetCount > 0 && <ContactTestDrivesLinks contactId={contact.id} />}
					<Trigger
						render={(props) => (
							<InvitationListAttachSingleContactDrawer
								contactId={contact.id}
								open={props.open}
								onClose={props.handleClose}
							/>
						)}
					>
						{(props) => (
							<IconButton
								{...props}
								title={t("Add contact to invitation list")}
								icon="calendar"
								type="button"
								size="small"
							/>
						)}
					</Trigger>
					<IconButton
						title={t("Archive")}
						icon="archive_2"
						type="button"
						size="small"
						onClick={() => archiveByTenantHandler.clickHandler(contact.id)}
					/>
					<TenantArchiveDrawer
						{...archiveByTenantHandler.drawerProps}
						title={t("Archive")}
						type="archive"
						service={contactService}
					/>
					<ConfirmationDialog
						{...archiveByTenantHandler.confirmationProps}
						heading={t("Archive")}
						labels={{ close: t("Close"), confirm: t("Confirm") }}
					>
						{t("Are you sure you want to archive this contact?")}
					</ConfirmationDialog>
					<Trigger
						render={(props) => <ContactUpdateBaseInfoDrawer {...props} onClose={props.handleClose} contact={contact} />}
					>
						{(props) => (
							<IconButton
								title={t("Edit contact")}
								icon="settings"
								type="button"
								size="small"
								onClick={props.onClick}
							/>
						)}
					</Trigger>
				</HeaderContent>
			</ViewPageSectionHeader>
			<ContactViewModuleSection>
				<AffiliationCountCard title={t("Event affiliation")}>
					{t("{{count}} events", { count: contact.affiliatedEventCount })}
				</AffiliationCountCard>
				<AffiliationCountCard title={t("Car fleet affiliation")}>
					{t("{{count}} car fleets", { count: contact.affiliatedCarFleetCount })}
				</AffiliationCountCard>
				<AffiliationCountCard title={t("Event app")}>
					{contact.lastAppLogin
						? t("Yes, last logged in on {{lastLogin}}", { lastLogin: formatDateTime(contact.lastAppLogin) })
						: t("No")}
				</AffiliationCountCard>
				<NewsletterListCard contact={contact} />
			</ContactViewModuleSection>
			<ViewPageSectionHeader>{t("General")}</ViewPageSectionHeader>
			<ViewPageSectionBody>
				<Trigger
					render={(props) => (
						<ContactUpdateDrawer
							{...props}
							onClosed={() => {}}
							id={contact.id}
							onClose={props.handleClose}
							dataFields={dataFields}
							contactViewId={contactViewId}
						/>
					)}
				>
					{(props) => (
						<ViewPageSectionListItem {...props}>
							<GeneralInfoContent>
								{attributes.map((attribute) => {
									if (attribute.entityType === "tenants") {
										return (
											<Row
												key={attribute.id}
												label={attribute.label}
												value={attribute.value
													.map((value) => {
														if (contact.archivedByTenants.some((x) => x.id === value.id)) {
															return `(${t("Archived")}) ${value.name}`;
														}
														return value.name;
													})
													.join(", ")}
											/>
										);
									}
									return <Row key={attribute.id} label={attribute.label} value={resolveAttributeValue(attribute)} />;
								})}
							</GeneralInfoContent>
						</ViewPageSectionListItem>
					)}
				</Trigger>
			</ViewPageSectionBody>
		</ViewPageSection>
	);
};
